import { appSettings } from 'bam-node-baseball-savant/leaderboard/appSettings'
import Table from 'bam-node-savant-table/table-ts.ts'
import type { FilterType } from 'bam-node-savant-table/table-ts.ts'

import { updateLabel } from 'bam-node-baseball-savant/leaderboard/utils/multi-select'
import {
    HotStoveBase,
    HotStoveTrackerBatter,
    HotStoveTrackerPitcher,
} from '../swing-path/types/globals.ts'
import 'bam-node-baseball-savant/leaderboard/utils/leaderboard-filter-helpers'
import {
    batterHeaders,
    batterSecondaryHeaders,
    pitcherHeaders,
    pitcherSecondaryHeaders,
} from './headers.ts'

const {
    MAGIC: { sameTeam, diffTeam },
} = appSettings.SETTINGS_HOT_STOVE

const initialBatterData = HOT_STOVE_BATTER_DATA.filter(
    (d) => d.pa >= HOT_STOVE_TRACKER_MIN_PA
)

const batterTable = new Table<HotStoveTrackerBatter>({
    id: 'hot-stove-batter-table-wrapper',
    data: initialBatterData,
    search: { columnsToSearch: ['player_name', 'name'] },
    headers: batterHeaders,
    secondaryHeaders: batterSecondaryHeaders,
    initialSortDirection: 'desc',
    defaultSort: 'swing_take_run_value',
    handleUrlUpdates: {
        enabled: true,
    },
})

const initialPitcherData = HOT_STOVE_PITCHER_DATA.filter(
    (d) => Number(d.p_formatted_ip) >= HOT_STOVE_TRACKER_MIN_IP
)

const pitcherTable = new Table<HotStoveTrackerPitcher>({
    id: 'hot-stove-pitcher-table-wrapper',
    data: initialPitcherData,
    search: { columnsToSearch: ['player_name', 'name'] },
    pagination: { rowsPerPage: 30 },
    initialSortDirection: 'desc',
    defaultSort: 'swing_take_run_value',
    headers: pitcherHeaders,
    secondaryHeaders: pitcherSecondaryHeaders,
    handleUrlUpdates: {
        enabled: true,
    },
})

const $batterTable = document.getElementById('hot-stove-batter-table-wrapper')
const $pitcherTable = document.getElementById('hot-stove-pitcher-table-wrapper')
// Player type
const pitcherPositions = ['SP', 'P', 'RP']
const $type = document.getElementById('ddlType') as HTMLSelectElement
const $hiddenType = document.getElementById(
    'hf-type'
) as HTMLInputElement | null
$type.addEventListener('change', (e) => {
    const { value } = e.target as HTMLSelectElement
    const isPitcher = value === 'pitcher'

    if ($hiddenType) {
        $hiddenType.value = value
    }

    if (!$batterTable || !$pitcherTable) {
        return
    }

    if (!isPitcher) {
        $batterTable.style.display = 'block'
        $pitcherTable.style.display = 'none'
    }
    if (isPitcher) {
        $batterTable.style.display = 'none'
        $pitcherTable.style.display = 'block'
    }

    const positionIsPitcher = pitcherPositions.includes($position.value) // eslint-disable-line no-use-before-define
    const isOhtani = $position.value === 'TWP' // eslint-disable-line no-use-before-define

    if (positionIsPitcher !== isPitcher && !isOhtani) {
        batterTable.removeColumnFilter('position')
        pitcherTable.removeColumnFilter('position')

        // eslint-disable-next-line no-use-before-define
        $position.value = ''
    }

    const url = new URL(window.location.href)
    url.searchParams.set('type', value)

    window.history.replaceState('', '', String(url))
})

// Since we have two tables that are both fighting to update the URL, my current hack is to have the primary table be the last to update the URL 🤮
function handleFilter(
    column: keyof HotStoveBase,
    value: string | number[] | boolean,
    comparator: FilterType = 'eq'
) {
    const isPitcher = $type.value === 'pitcher'
    const primaryTable = isPitcher ? pitcherTable : batterTable
    const secondaryTable = isPitcher ? batterTable : pitcherTable

    const isBoolean = typeof value === 'boolean'

    const shouldUpdate = isBoolean || (value && value?.length)

    if (shouldUpdate) {
        secondaryTable.updateColumnFilter(column, comparator, value)
        primaryTable.updateColumnFilter(column, comparator, value)
    } else {
        secondaryTable.removeColumnFilter(column)
        primaryTable.removeColumnFilter(column)
    }
}

// Pitch Hand
const $pitchHand = document.getElementById('ddlPitchHand') as HTMLSelectElement
$pitchHand.addEventListener('change', (e) => {
    const { value } = e.target as HTMLSelectElement

    handleFilter('pitch_hand', value, 'eq')
})

// Bat Side
const $batSide = document.getElementById('ddlBatSide') as HTMLSelectElement
$batSide.addEventListener('change', (e) => {
    const { value } = e.target as HTMLSelectElement

    handleFilter('bat_side', value, 'eq')
})

// Position
const $position = document.getElementById('ddlPosition') as HTMLSelectElement
$position.addEventListener('change', (e) => {
    const { value } = e.target as HTMLSelectElement

    handleFilter('position', value)

    if (!$batterTable || !$pitcherTable || !value || value === 'TWP') {
        return
    }

    // Flip the type of it's relevant
    const evt = new Event('change')
    if (pitcherPositions.includes(value) && $type.value === 'batter') {
        $type.value = 'pitcher'
        $type.dispatchEvent(evt)
    } else if (!pitcherPositions.includes(value) && $type.value === 'pitcher') {
        $type.value = 'batter'
        $type.dispatchEvent(evt)
    }
})

const $minPa = document.getElementById('ddlMinPa') as HTMLInputElement
$minPa.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement
    const value = target.valueAsNumber

    const filteredData = HOT_STOVE_BATTER_DATA.filter((d) => d.pa >= value)

    batterTable.updateData(filteredData)

    const url = new URL(window.location.href)
    url.searchParams.set('min-pa', String(value))

    window.history.replaceState('', '', String(url))
})

const $minIp = document.getElementById('ddlMinIp') as HTMLInputElement
$minIp.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement
    const value = target.valueAsNumber

    const filteredData = HOT_STOVE_PITCHER_DATA.filter(
        (d) => Number(d.p_formatted_ip) >= value
    )

    pitcherTable.updateData(filteredData)

    const url = new URL(window.location.href)
    url.searchParams.set('min-ip', String(value))

    window.history.replaceState('', '', String(url))
})

// Transaction Type (might not exist)
const $transactionType = document.getElementById(
    'ddlTransactionType'
) as HTMLSelectElement | null
$transactionType?.addEventListener('change', (e) => {
    const { value } = e.target as HTMLSelectElement

    handleFilter('transaction_type', value)
})

// Handle update of free agent team
document.addEventListener(
    'multi-select-updated',
    ({ detail: { values }, target }) => {
        if (!target) {
            return
        }

        let teamIds = values.map(Number)

        const hasSameTeam = Boolean(teamIds.find((d) => d === sameTeam))
        const hasDiffTeam = Boolean(teamIds.find((d) => d === diffTeam))

        teamIds = teamIds.filter((d) => d !== diffTeam && d !== sameTeam)

        const modal = target as Element
        if (modal.closest('#hot-stove-previous-team')) {
            handleFilter('original_team_id', teamIds, 'in')
        } else if (modal.closest('#hot-stove-next-team')) {
            handleFilter('new_team_id', teamIds, 'in')

            // same team vs different team has some strained logic
            if (hasSameTeam && hasDiffTeam) {
                // if both, we want to make sure that the diff_team column is not null (which indicates unsigned)
                handleFilter('diff_team', '0', 'not_null')
                return
            }

            if (hasSameTeam) {
                handleFilter('diff_team', false, 'eq')
                return
            }
            if (hasDiffTeam) {
                handleFilter('diff_team', true, 'eq')
                return
            }

            // Make sure to remove the column filter by passing a falsy value through (that isn't a boolean! yikes)
            handleFilter('diff_team', '', 'eq')
        }
    }
)

document.addEventListener('click', (e) => {
    const $tooltipWrapper = (e.target as HTMLElement).closest(
        '.hot-stove-tooltip-wrapper'
    )
    const tooltip = $tooltipWrapper?.querySelector('.hot-stove-tooltip')
    const showTooltip =
        tooltip && !tooltip.classList.contains('hot-stove-tooltip-show')

    const $tooltips = document.querySelectorAll('.hot-stove-tooltip')
    $tooltips.forEach((el) => el.classList.remove('hot-stove-tooltip-show'))

    if (showTooltip) {
        tooltip.classList.add('hot-stove-tooltip-show')
    }
})

const prevTeamModal = document.querySelector(
    '#hot-stove-previous-team .savant-multi-select-modal'
)
const nextTeamModal = document.querySelector(
    '#hot-stove-next-team .savant-multi-select-modal'
)

document.getElementById('btnClear')?.addEventListener('click', () => {
    pitcherTable.resetFilters()
    batterTable.resetFilters()

    // Manual bits, gross
    $position.value = ''
    $batSide.value = ''
    $pitchHand.value = ''

    if ($transactionType) {
        $transactionType.value = ''
    }

    if (prevTeamModal && nextTeamModal) {
        prevTeamModal
            .querySelectorAll<HTMLInputElement>('input')
            .forEach((el) => {
                el.checked = false // eslint-disable-line no-param-reassign
            })
        nextTeamModal
            .querySelectorAll<HTMLInputElement>('input')
            .forEach((el) => {
                el.checked = false // eslint-disable-line no-param-reassign
            })

        updateLabel(prevTeamModal, true)
        updateLabel(nextTeamModal, true)
    }
})
